import React, { useState } from 'react';
import { X, Upload, FileText, Camera } from 'lucide-react';
import { getStorageInstance, getDB, isFirebaseConfigured } from '../../lib/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { COLLECTIONS } from '../../lib/database.types';

interface UploadDocumentModalProps {
  caseId: string;
  onClose: () => void;
  onDocumentUploaded: () => void;
}

export function UploadDocumentModal({ caseId, onClose, onDocumentUploaded }: UploadDocumentModalProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [documentType, setDocumentType] = useState('');
  const [description, setDescription] = useState('');

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload only images, PDFs, or text files');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setUploadProgress(0);

      if (!isFirebaseConfigured()) {
        // Mock upload for development
        console.log('Mock: Would upload document:', {
          caseId,
          fileName: file.name,
          fileSize: file.size,
          documentType,
          description
        });
        
        // Simulate upload progress
        for (let i = 0; i <= 100; i += 10) {
          setUploadProgress(i);
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        onDocumentUploaded();
        onClose();
        return;
      }

      // Create a reference to the file in Firebase Storage
      const fileName = `documents/${caseId}/${Date.now()}-${file.name}`;
      const storageRef = ref(getStorageInstance(), fileName);

      // Upload file with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Upload error:', error);
          setError('Failed to upload file. Please try again.');
          setUploading(false);
        },
        async () => {
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

            // Save document metadata to Firestore
            await addDoc(collection(getDB(), COLLECTIONS.DOCUMENTS), {
              case_id: caseId,
              file_name: file.name,
              file_url: downloadURL,
              file_size: file.size,
              file_type: file.type,
              document_type: documentType,
              description: description,
              uploaded_at: serverTimestamp()
            });

            onDocumentUploaded();
            onClose();
          } catch (error) {
            console.error('Error saving document metadata:', error);
            setError('Failed to save document information. Please try again.');
          } finally {
            setUploading(false);
          }
        }
      );
    } catch (error) {
      console.error('Error uploading document:', error);
      setError('Failed to upload document. Please try again.');
      setUploading(false);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileUpload(e.target.files);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Upload Document</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Document Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Type
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={uploading}
            >
              <option value="">Select document type</option>
              <option value="invoice">Invoice</option>
              <option value="receipt">Receipt</option>
              <option value="photo">Photo</option>
              <option value="report">Report</option>
              <option value="correspondence">Correspondence</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the document"
              rows={2}
              className="w-full p-2 border border-gray-300 rounded-md"
              disabled={uploading}
            />
          </div>

          {/* File Upload Area */}
          {!uploading ? (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                dragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="fileInput"
                className="hidden"
                accept="image/*,.pdf,.txt"
                onChange={handleFileSelect}
              />
              
              <div className="space-y-2">
                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                <p className="text-sm text-gray-600">
                  Drag and drop a file here, or{' '}
                  <label htmlFor="fileInput" className="text-blue-600 hover:text-blue-700 cursor-pointer">
                    browse
                  </label>
                </p>
                <p className="text-xs text-gray-400">
                  Supports: Images, PDF, Text files (max 10MB)
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            disabled={uploading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
} 